import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowLeft, Calendar, Clock, TrendingUp, TrendingDown, BarChart3, Download, FileText, Image } from "lucide-react";
import { useAnalysisDetail } from '@/hooks/useAnalysisHistory';
import { EnhancedAiRecommendationCard } from '@/components/market-analysis/cards/EnhancedAiRecommendationCard';
import { TrendsAnalysisCard } from '@/components/market-analysis/cards/TrendsAnalysisCard';
import { KeyLevelsCard } from '@/components/market-analysis/cards/KeyLevelsCard';
import { PriceTargetsCard } from '@/components/market-analysis/cards/PriceTargetsCard';
import { AnalysisSummaryCard } from '@/components/market-analysis/cards/AnalysisSummaryCard';
import { MarketAnalysisExportService } from '@/services/MarketAnalysisExportService';
import { ScreenshotExportService } from '@/services/ScreenshotExportService';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';
import '@/components/dashboard/dashboard-animations.css';

const MarketAnalysisDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, isLoading, error } = useAnalysisDetail(id!);
  const isMobile = useIsMobile();
  const [isExporting, setIsExporting] = React.useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-700 border-green-200';
      case 'PROCESSING': 
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'FAILED': return 'bg-red-100 text-red-700 border-red-200';
      case 'RATE_LIMITED': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'BUY': return <TrendingUp className="h-5 w-5" />;
      case 'SELL': return <TrendingDown className="h-5 w-5" />;
      default: return <BarChart3 className="h-5 w-5" />;
    }
  };

  const getRecommendationAction = (): string => {
    try {
      const aiRecommendation = session?.card_results?.ai_recommendation;
      if (aiRecommendation?.status === 'completed' && aiRecommendation?.data) {
        return aiRecommendation.data.action || 'HOLD';
      }
    } catch (error) {
      console.error('Error parsing AI recommendation:', error);
    }
    return 'HOLD';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      fullDate: date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getPriceTargetsData = () => {
    const priceTargetsResult = session?.card_results?.price_targets;
    console.log('Debug - Full price_targets result:', priceTargetsResult);
    console.log('Debug - price_targets data:', priceTargetsResult?.data);
    
    if (priceTargetsResult?.status === 'completed' && priceTargetsResult?.data) {
      // Check if data has priceTargets property or if it's the data itself
      const data = priceTargetsResult.data.priceTargets || priceTargetsResult.data;
      console.log('Debug - Processed priceTargets data:', data);
      
      // Ensure we have the expected structure with fallbacks
      return {
        priceTargets: {
          bullish: data?.bullish || 0,
          bearish: data?.bearish || 0
        },
        confidence: priceTargetsResult.data.confidence || data?.confidence || 0
      };
    }
    
    return null;
  };

  // Helper function to check if a step result is available
  const isStepResultAvailable = (stepKey: string) => {
    return session?.card_results?.[stepKey]?.status === 'completed';
  };

  // Helper function to get step result data
  const getStepResultData = (stepKey: string) => {
    return session?.card_results?.[stepKey]?.data;
  };

  const handleExportPDF = async () => {
    if (!session) return;
    
    setIsExporting(true);
    try {
      await MarketAnalysisExportService.exportAnalysisToPDF(session);
      toast.success('Analysis exported as PDF successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportScreenshot = async () => {
    if (!session) return;
    
    setIsExporting(true);
    try {
      await ScreenshotExportService.exportAnalysisAsScreenshot(session, 'analysis-content');
      toast.success('Analysis exported as image successfully');
    } catch (error) {
      console.error('Screenshot export error:', error);
      toast.error('Failed to export screenshot');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analysis details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-red-500 mb-4">Analysis session not found</div>
            <Button onClick={() => navigate('/market-analysis/history')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to History
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const action = getRecommendationAction();
  const { fullDate, time } = formatDate(session.created_at);
  const priceTargetsData = getPriceTargetsData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-6 relative overflow-hidden">
      {/* Enhanced animated background particles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-emerald-500/20 rounded-full animate-pulse floating-action" />
        <div className="absolute top-40 right-20 w-1 h-1 bg-blue-500/30 rounded-full animate-pulse floating-action animate-delay-100" />
        <div className="absolute bottom-40 left-20 w-1.5 h-1.5 bg-purple-500/20 rounded-full animate-pulse floating-action animate-delay-200" />
        <div className="absolute bottom-20 right-10 w-1 h-1 bg-amber-500/30 rounded-full animate-pulse floating-action animate-delay-300" />
        <div className="absolute top-1/2 left-1/4 w-1 h-1 bg-rose-500/20 rounded-full animate-pulse floating-action animate-delay-400" />
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-cyan-500/25 rounded-full animate-pulse floating-action animate-delay-500" />
      </div>

      <div className="max-w-7xl mx-auto space-y-8 relative z-10" data-analysis-content id="analysis-content">
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-in">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/market-analysis/history')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to History
          </Button>

          {/* Export Button - Only show for completed analyses */}
          {session.status === 'COMPLETED' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={isExporting}>
                  <Download className="h-4 w-4 mr-2" />
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleExportPDF}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportScreenshot}>
                  <Image className="h-4 w-4 mr-2" />
                  Export as Image
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Session Info */}
        <div className="animate-fade-in animate-delay-100">
          <Card className="bg-gradient-to-br from-background via-background to-muted/20 border border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {getActionIcon(action)}
                    <CardTitle className="text-2xl">{session.symbol} Analysis</CardTitle>
                  </div>
                  <Badge className={getStatusColor(session.status)}>
                    {session.status}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  Progress: {session.progress_percentage}%
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{fullDate}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{time}</span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Steps: </span>
                  <span>{session.current_step} of {session.total_steps}</span>
                </div>
              </div>
              {session.status_message && (
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground italic">{session.status_message}</p>
                </div>
              )}
              {session.error_message && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{session.error_message}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Analysis Results - Following the same order as MarketAnalysisPage */}
        {session.status === 'COMPLETED' && (
          <div className="space-y-6">
            {/* THỨ TỰ 1: Key Levels and Price Targets - Two Column Layout */}
            <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
              {/* Key Levels - Show if available */}
              {isStepResultAvailable('key_levels') && (
                <div className="animate-fade-in animate-delay-300">
                  <KeyLevelsCard supportResistance={getStepResultData('key_levels')} />
                </div>
              )}

              {/* Price Targets - Show if available */}
              {priceTargetsData && (
                <div className="animate-fade-in animate-delay-350">
                  <PriceTargetsCard 
                    priceTargets={priceTargetsData.priceTargets}
                    confidence={priceTargetsData.confidence}
                  />
                </div>
              )}
            </div>

            {/* THỨ TỰ 2: Trends Analysis - Full Width Card */}
            {isStepResultAvailable('trends_analysis') && (
              <div className="animate-fade-in animate-delay-400">
                <TrendsAnalysisCard trends={getStepResultData('trends_analysis')} />
              </div>
            )}

            {/* THỨ TỰ 3: AI Recommendation - Enhanced Version */}
            {isStepResultAvailable('ai_recommendation') && (
              <div className="animate-fade-in animate-delay-500">
                <EnhancedAiRecommendationCard 
                  data={getStepResultData('ai_recommendation')}
                />
              </div>
            )}

            {/* THỨ TỰ 4: Analysis Summary - Show if available */}
            {isStepResultAvailable('analysis_summary') && (
              <div className="animate-fade-in animate-delay-600">
                <AnalysisSummaryCard 
                  riskLevel={getStepResultData('analysis_summary').riskLevel}
                  analysis={getStepResultData('analysis_summary').summary}
                />
              </div>
            )}
          </div>
        )}

        {/* Incomplete Analysis Message */}
        {session.status !== 'COMPLETED' && (
          <Card className="bg-gradient-to-br from-background via-background to-muted/20 border border-border/50 animate-fade-in animate-delay-200">
            <CardContent className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {session.status === 'FAILED' ? 'Analysis Failed' : 'Analysis In Progress'}
              </h3>
              <p className="text-muted-foreground">
                {session.status === 'FAILED' 
                  ? 'This analysis session encountered an error and could not be completed.'
                  : 'This analysis is still being processed. Results will appear here once completed.'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MarketAnalysisDetailPage;
